import { Box, Button, InputAdornment, Paper, SxProps, Theme, Typography } from "@mui/material";
import DatePicker from "../../components/form/DatePicker";
import moment from "moment";
import { useState } from "react";
import route from "../../Router";
import { iconByName } from "../../components/IconPicker";
import { AbstractTypedDataForm, DataFormProps } from "../../components/data/DataForm";
import { FormNumberInput, TextInput } from "../../components/form/TextInput";
import { useCachedStore } from "../../models/Hooks";
import { Measure } from "../../models/Types";
import { getTheme } from "../../theme";
import { isMobile } from "../../utils";
import HouseCombo, { HouseToggle } from "../houses/HouseCombo";
import { tariffStore } from "../project/tariff/TariffCombo";
import MeterTariffCombo, { meterTariffStore } from "./MeterTariffCombo";

route.register('MeterTariffId', undefined, undefined, parseInt);

export function measureParse(d?: any) {
    const dto = (d || {}) as Measure;
    d?.CreateDate && (dto.CreateDate = new Date(d.CreateDate));
    d?.ReadDate && (dto.ReadDate = new Date(d.ReadDate));
    return dto;
}

class DesktopForm<T extends Measure = Measure> extends AbstractTypedDataForm<T, {}, { houseId?: number }> {
    protected getTypeId(): string {
        return 'Measure';
    }
    protected getTypeName(): React.ReactNode {
        return 'Показание';
    }

    buildButtons() {
        return [];
    }

    protected createEntity() {
        const result = measureParse() as T;
        result.MeterTariffId = route.get<number>('MeterTariffId');
        return result;
    }

    protected onFieldChange(field: string, value: any) {
        if (field === 'HouseId') {
            this.setState({ houseId: parseInt(value as string) || undefined });
        }

        return super.onFieldChange(field, value);
    }

    protected buildItems(entity?: Measure | undefined): React.ReactNode {
        return <>
            <Paper sx={{ minWidth: 500 }} style={{ overflow: 'auto' }}>
                {/*<Box display="flex" flexDirection="column" gap={2} overflow="auto">*/}
                <TextInput
                    value={entity?.HouseId}
                    label="Домвладение"
                    name="HouseId"
                    onChange={this.onChange}
                    component={(onChange, error) => <HouseCombo
                        name="HouseId"
                        error={!!error}
                        selectFirstOnLoad={entity && !entity.Id}
                        filter={x => !!meterTariffStore.data?.find(mt => mt.Obj?.HouseId === x.Id)}
                        value={entity?.HouseId}
                        onChange={onChange} />} />

                <TextInput
                    required={true}
                    value={entity?.MeterTariffId}
                    label="Ресурс"
                    name="MeterTariffId"
                    onChange={this.onChange}
                    validator={v => v ? undefined : 'Необходимо выбрать ресурс'}
                    refValidator={val => this.addValidator('MeterTariffId', 'MeterTariffId', v => val(v))}
                    component={(onChange, error) => <MeterTariffCombo
                        name="MeterTariffId"
                        error={!!error}
                        filter={x => !this.state?.houseId || (x.Obj?.HouseId === this.state.houseId)}
                        value={entity?.MeterTariffId}
                        onChange={onChange} />} />

                <DatePicker label="Дата подачи" value={entity?.CreateDate} readOnly />
                <DatePicker label="Дата снятия" value={entity?.ReadDate} onChange={v => entity!.ReadDate = v?.toDate() || new Date()} />

                <FormNumberInput label="Значение" name="Value" form={this} entity={entity} />
                {/*</Box>*/}

                <div style={{ flex: 1 }} />

                <Box sx={{ display: 'flex' }}>
                    <Button variant="contained" type="submit">Сохранить</Button>
                </Box>
            </Paper>
        </>;
    }
}

type ComplexMeasure = {
    Measures: {
        [meterTariffId: number]: number
    }
} & Measure;

class MobileForm extends DesktopForm<ComplexMeasure> {
    protected getFormStyle(): SxProps<Theme> | undefined {
        return {
            ...super.getFormStyle(),
            flexDirection: 'column',
            alignItems: 'stretch'
        };
    }

    protected createEntity() {
        const result = super.createEntity() as ComplexMeasure;
        result.Measures = {};
        return result;
    }

    getCmdPost() {
        return 'MeasureSend'
    }

    protected buildItems(entity?: ComplexMeasure | undefined): React.ReactNode {
        return <>
            <Paper sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }} style={{ overflow: 'auto' }}>
                <Typography variant="h2">Новые показания от {moment().format('D.MM.y')}</Typography>

                <HouseToggle
                    name="HouseId"
                    selectFirstOnLoad={entity && !entity.Id}
                    filter={x => true || !!meterTariffStore.data?.find(mt => mt.Obj?.HouseId === x.Id)}
                    value={entity?.HouseId}
                    onChange={this.onChange} />

                {entity && meterTariffStore?.data?.filter(x => !this.state?.houseId || (x.Obj?.HouseId === this.state.houseId))
                    .map(x => <MeasureInput measures={entity?.Measures} name={x.Id} onChange={this.onChange} />)}

                <div style={{ flex: 1 }} />

                <Box sx={{ display: 'flex' }}>
                    <Button variant="contained" type="submit">Отправить</Button>
                </Box>
            </Paper>
        </>;
    }
}

function MeasureInput({ name, measures, onChange }: { name: number, onChange: (e: any) => any, measures: { [id: number]: number | undefined } }) {
    const [v, sv] = useState(measures[name]);
    const obj = meterTariffStore.data!.find(x => x.Id == name)!;

    return <TextInput
        value={v}
        label={obj.Value}
        name={name}
        onChange={e => {
            const v = parseInt(e.target.value) || undefined;
            sv(v);
            v ? measures[name] = v : (delete measures[name]);
            //onChange(e);
        }}
        inputProps={{
            type: 'number',
            sx: v ? {
                "& > .MuiOutlinedInput-notchedOutline": {
                    borderColor: getTheme().palette.primary.main
                }
            } : undefined,
            endAdornment: <InputAdornment position="end">
                {iconByName(tariffStore.data?.find(t => t.Id == obj.Obj?.TariffId)?.Obj?.Icon, { fontSize: 'small', color: v ? 'primary' : 'disabled' })}
            </InputAdornment>
        }}
    />;
}

export function MeasureForm(props: { id: number, houseId?: number } & DataFormProps<Measure>) {
    useCachedStore(meterTariffStore);
    useCachedStore(tariffStore);

    return isMobile() ? <MobileForm {...props} /> : <DesktopForm {...props} />;
}